import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { BucketKey, CostLine, MaandStapel } from '@fleet/shared';
import {
  allMonths,
  buildPersoonKengetallen,
  catToBucket,
  euro,
  fmtDate,
  groepeerKosten,
  kiesPeriode,
  kostenPerBucket,
  kostenPerMaand,
  kostenTotaal,
  verbruik,
} from '@fleet/shared';
import { AutoLink } from '../../components/ui/EntiteitLink';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { Panel } from '../../components/ui/Panel';
import { TrackerRow } from '../dashboard/TrackerRow';

/**
 * De variabele kosten van één persoon: kengetallen, het verloop per maand, de
 * verdeling over kostenposten, en daaronder het antwoord op "waar komt dit
 * vandaan".
 *
 * Drie lagen die elkaar filteren. De periode bovenaan bepaalt waar alles over
 * gaat; een klik op een maandstaaf zet die periode. Een klik op een kostenpost
 * opent het detail eronder. Zo loop je van "augustus was duur" naar "dat kwam
 * door laden" naar "en dat was drie keer bij dezelfde snellader".
 *
 * Alles rekent uit de transacties die de API al meestuurt. Onderhoud zit er niet
 * bij: dat staat op kenteken zonder pas en hangt dus aan de auto.
 */

/** Zoveel maanden in de staafgrafiek; een jaar geeft het seizoen mee. */
const MAANDEN_IN_BEELD = 12;
const TRANSACTIES_ZICHTBAAR = 8;
const GROEPEN_ZICHTBAAR = 4;

const chip = (on: boolean): CSSProperties => ({
  background: on ? '#2A6085' : '#FFFFFF',
  color: on ? '#FFFFFF' : '#2A6085',
  border: `1px solid ${on ? '#2A6085' : '#C9BBA8'}`,
  borderRadius: 2,
  padding: '5px 11px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Roboto, sans-serif',
});

const kopje: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#888888',
  marginBottom: 6,
};

/** De laatste `aantal` maanden tot en met `eind`, ook de lege ertussen. */
function maandVenster(eind: string, aantal: number): string[] {
  const [j, m] = eind.split('-').map(Number);
  if (!j || !m) return [];
  const uit: string[] = [];
  for (let i = aantal - 1; i >= 0; i--) {
    const idx = j * 12 + (m - 1) - i;
    uit.push(`${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`);
  }
  return uit;
}

/** Gestapelde staven per maand; klikken kiest die maand als periode. */
function MaandGrafiek({
  stapels,
  actief,
  onKies,
}: {
  stapels: MaandStapel[];
  actief: string | null;
  onKies: (maand: string) => void;
}) {
  const max = Math.max(1, ...stapels.map((s) => s.totaal));
  return (
    <div>
      <div style={kopje}>Per maand</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 120 }}>
        {stapels.map((s) => {
          const aan = actief === s.maand;
          return (
            <button
              key={s.maand}
              type="button"
              onClick={() => onKies(s.maand)}
              title={`${s.label}: ${euro(s.totaal)}`}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: 2,
                height: '100%',
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${aan ? '#2A6085' : 'transparent'}`,
                padding: 0,
                cursor: 'pointer',
                // Een maand zonder kosten houdt zijn plek op de as, anders
                // verdwijnt juist de maand waarin niets gebeurde.
                opacity: s.totaal === 0 ? 0.35 : aan || actief === null ? 1 : 0.55,
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column-reverse', height: '100%' }}>
                {s.delen.map((d) => (
                  <span
                    key={d.key}
                    style={{
                      display: 'block',
                      background: d.color,
                      height: `${(d.bedrag / max) * 100}%`,
                    }}
                  />
                ))}
              </span>
              <span style={{ fontSize: 10, color: '#888888' }}>{s.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Eén regel in een groepslijstje: naam, bedrag, aantal. */
function GroepRij({ naam, bedrag, aantal }: { naam: string; bedrag: number; aantal: number }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {naam}
      </span>
      <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{euro(bedrag)}</span>
      <span style={{ color: '#999999', whiteSpace: 'nowrap' }}>{aantal}×</span>
    </div>
  );
}

function Groep({
  titel,
  rijen,
}: {
  titel: string;
  rijen: { naam: string; bedrag: number; aantal: number }[];
}) {
  if (!rijen.length) return null;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={kopje}>{titel}</div>
      <MeerTonen items={rijen} cap={GROEPEN_ZICHTBAAR} meer={(n) => `+${n} meer`}>
        {(zicht) => (
          <div>
            {zicht.map((r) => (
              <GroepRij key={r.naam} {...r} />
            ))}
          </div>
        )}
      </MeerTonen>
    </div>
  );
}

const TCOLS = '82px 46px 90px 1fr 92px 78px 1fr';

export function VariabeleKosten({
  costLines,
  filter,
  onFilter,
}: {
  costLines: CostLine[];
  /** `null` = alles, `'YTD'` = dit jaar, anders een `YYYY-MM`. */
  filter: string | null;
  onFilter: (filter: string | null) => void;
}) {
  const [bucket, setBucket] = useState<BucketKey | null>(null);

  const months = useMemo(() => allMonths(costLines), [costLines]);
  const periode = useMemo(() => kiesPeriode(costLines, filter), [costLines, filter]);
  const tegels = useMemo(
    () => buildPersoonKengetallen({ regels: costLines, filter }),
    [costLines, filter],
  );
  const stapels = useMemo(() => {
    const eind = months[months.length - 1];
    return eind ? kostenPerMaand(costLines, maandVenster(eind, MAANDEN_IN_BEELD)) : [];
  }, [costLines, months]);
  const posten = useMemo(() => kostenPerBucket(periode.regels), [periode.regels]);

  const detail = useMemo(() => {
    if (!bucket) return null;
    const regels = periode.regels.filter((l) => catToBucket(l.categorie) === bucket);
    if (!regels.length) return null;
    const kentekens = new Set(regels.map((l) => l.kenteken));
    return {
      regels: [...regels].sort((a, b) => ((a.datum ?? '') < (b.datum ?? '') ? 1 : -1)),
      bedrag: kostenTotaal(regels),
      liters: verbruik(regels, 'LTR'),
      kwh: verbruik(regels, 'KWH'),
      perCategorie: groepeerKosten(regels, (l) => l.categorie),
      perLocatie: groepeerKosten(regels, (l) => l.station ?? l.plaats),
      perAuto: kentekens.size > 1 ? groepeerKosten(regels, (l) => l.kenteken) : [],
    };
  }, [bucket, periode.regels]);

  const post = posten.find((p) => p.key === bucket);
  const maxPost = Math.max(1, ...posten.map((p) => p.bedrag));
  const totaal = kostenTotaal(periode.regels);

  if (!costLines.length) {
    return (
      <Panel>
        <Eyebrow>Variabele kosten</Eyebrow>
        <div style={{ fontSize: 13, color: '#888888', marginTop: 8 }}>
          Nog geen transacties op de pas van deze persoon.
        </div>
      </Panel>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>Variabele kosten</Eyebrow>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#2A6085', marginTop: 4 }}>
            {euro(totaal)}
            <span style={{ fontSize: 13, fontWeight: 400, color: '#888888' }}>
              {' '}
              · {periode.label}
            </span>
          </div>
        </div>
      </div>

      <TrackerRow trackers={tegels} perAutoMogelijk={false} />

      <Panel style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <MaandGrafiek
          stapels={stapels}
          actief={filter && filter !== 'YTD' ? filter : null}
          onKies={(m) => onFilter(m === filter ? null : m)}
        />

        <div>
          <div style={kopje}>Per kostenpost · klik voor de details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {posten.length === 0 && (
              <div style={{ fontSize: 13, color: '#888888' }}>
                Geen transacties in {periode.label}.
              </div>
            )}
            {posten.map((p) => {
              const aan = bucket === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setBucket(aan ? null : p.key)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '110px minmax(0, 1fr) 92px 46px',
                    alignItems: 'center',
                    gap: 12,
                    background: 'none',
                    border: 'none',
                    borderLeft: `3px solid ${aan ? '#2A6085' : 'transparent'}`,
                    padding: '2px 0 2px 8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: aan ? 700 : 400, color: '#111111' }}>
                    {p.label}
                  </span>
                  <span style={{ background: '#F0E8DC', height: 18, borderRadius: 3 }}>
                    <span
                      style={{
                        display: 'block',
                        height: '100%',
                        borderRadius: 3,
                        background: p.color,
                        width: `${(p.bedrag / maxPost) * 100}%`,
                      }}
                    />
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
                    {euro(p.bedrag)}
                  </span>
                  <span style={{ fontSize: 12, color: '#999999', textAlign: 'right' }}>
                    {Math.round((p.bedrag / (totaal || 1)) * 100)}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Panel>

      {detail && post && (
        <Panel style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <Eyebrow color={post.color === '#BCD4DA' ? '#2A6085' : post.color}>
                {post.label} · {periode.label}
              </Eyebrow>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#111111', marginTop: 4 }}>
                {euro(detail.bedrag)}
                <span style={{ fontSize: 13, fontWeight: 400, color: '#888888' }}>
                  {' '}
                  in {detail.regels.length}{' '}
                  {detail.regels.length === 1 ? 'transactie' : 'transacties'} · gemiddeld{' '}
                  {euro(detail.bedrag / detail.regels.length)}
                  {detail.liters.waarde > 0 &&
                    ` · ${detail.liters.waarde.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} L à ${euro(detail.liters.prijs ?? 0)}`}
                  {detail.kwh.waarde > 0 &&
                    ` · ${detail.kwh.waarde.toLocaleString('nl-NL', { maximumFractionDigits: 1 })} kWh à ${euro(detail.kwh.prijs ?? 0)}`}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBucket(null)}
              style={{ ...chip(false), border: 'none' }}
              aria-label="Details sluiten"
            >
              Sluiten
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 20,
            }}
          >
            <Groep titel="Wat precies" rijen={detail.perCategorie} />
            <Groep titel="Waar" rijen={detail.perLocatie} />
            <Groep titel="Op welke auto" rijen={detail.perAuto} />
          </div>

          <div>
            <div style={kopje}>Transacties</div>
            <div style={{ overflowX: 'auto' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: TCOLS,
                  gap: 10,
                  padding: '6px 0',
                  borderBottom: '2px solid #111111',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#888888',
                  minWidth: 700,
                }}
              >
                <span>Datum</span>
                <span>Tijd</span>
                <span>Auto</span>
                <span>Wat</span>
                <span style={{ textAlign: 'right' }}>Hoeveelheid</span>
                <span style={{ textAlign: 'right' }}>Bedrag</span>
                <span>Locatie</span>
              </div>
              <MeerTonen
                items={detail.regels}
                cap={TRANSACTIES_ZICHTBAAR}
                meer={(n) => `+${n} transacties tonen`}
              >
                {(zicht) => (
                  <div>
                    {zicht.map((l) => (
                      <div
                        key={l.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: TCOLS,
                          gap: 10,
                          padding: '7px 0',
                          borderBottom: '1px solid #F0E8DC',
                          fontSize: 12.5,
                          alignItems: 'center',
                          minWidth: 700,
                        }}
                      >
                        <span style={{ color: '#666666' }}>{l.datum ? fmtDate(l.datum) : '—'}</span>
                        <span style={{ color: '#999999' }}>{l.tijd ?? ''}</span>
                        <AutoLink kenteken={l.kenteken} style={{ fontSize: 12 }} />
                        <span
                          style={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {l.product ?? l.categorie}
                        </span>
                        <span style={{ textAlign: 'right', color: '#666666' }}>
                          {l.hoeveelheid
                            ? `${l.hoeveelheid.toLocaleString('nl-NL', { maximumFractionDigits: 2 })} ${l.eenheid ?? ''}`
                            : ''}
                        </span>
                        <span style={{ textAlign: 'right', fontWeight: 700 }}>
                          {euro(l.bedragExcl)}
                        </span>
                        <span
                          style={{
                            color: '#666666',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {[l.station, l.plaats].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </MeerTonen>
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
